/**
 * GLTF/GLB support for the standalone Fragments viewer.
 *
 * @thatopen/fragments only handles `.frag` buffers. When a folder also
 * contains glTF/GLB models, those URLs must be loaded via THREE.GLTFLoader
 * and added to the scene directly. This module provides:
 *
 *   - inferViewerModelKind(entry)          → 'frag' | 'gltf'
 *   - loadGltfFromUrl({...})               → THREE.Group placed in the scene
 *   - attachGltfMeshesToRendererClipping() → make GLTF respect clip planes
 *   - disposeGltfSubtree(root)             → free resources
 *
 * Detection order: explicit `modelType` from app.html metadata, then file
 * extension (`.gltf`/`.glb`) on URL or display name. Anything else is
 * assumed to be a Fragments file.
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

export type ViewerModelKind = "frag" | "gltf";

export interface ViewerModelEntry {
  url?: string;
  name?: string;
  modelType?: string;
}

export function inferViewerModelKind(entry: ViewerModelEntry): ViewerModelKind {
  const mt = String(entry.modelType || "").toLowerCase().trim();
  if (mt === "gltf" || mt === "glb") return "gltf";
  if (mt === "frag" || mt === "fragments") return "frag";

  const u = String(entry.url || "").toLowerCase();
  const n = String(entry.name || "").toLowerCase();
  const hasGltfExt = (s: string) =>
    s.endsWith(".glb") ||
    s.endsWith(".gltf") ||
    s.includes(".glb?") ||
    s.includes(".gltf?");
  if (hasGltfExt(u) || hasGltfExt(n)) return "gltf";
  return "frag";
}

/**
 * Wire every material of `root` to the renderer's live clipping planes
 * array so OBC.Clipper planes also clip GLTF meshes.
 *
 * The renderer's `clippingPlanes` is mutated in-place by OBC.Clipper, so
 * sharing the reference is sufficient — no need to re-bind on each plane
 * change.
 */
export function attachGltfMeshesToRendererClipping(
  root: THREE.Object3D,
  renderer: THREE.WebGLRenderer
): void {
  if (!Array.isArray(renderer.clippingPlanes)) {
    renderer.clippingPlanes = [];
  }
  const planes = renderer.clippingPlanes as THREE.Plane[];
  root.traverse((child: THREE.Object3D) => {
    const mesh = child as THREE.Mesh;
    if (!(mesh as any).isMesh) return;
    const mat = mesh.material;
    if (!mat) return;
    const list = Array.isArray(mat) ? mat : [mat];
    for (const m of list as THREE.Material[]) {
      m.clippingPlanes = planes;
      m.clipShadows = false;
      m.needsUpdate = true;
    }
  });
}

/**
 * Download + parse a glTF/GLB and add it to the scene. Returns the
 * `THREE.Group` so callers can track it (e.g. in a model-id keyed map for
 * the Models Tree).
 *
 * The returned object has:
 *   - root.name = modelId
 *   - root.userData.echovViewerModelKind = "gltf"
 *   - frustumCulled = false on every descendant (matches the FRAGS path
 *     which also disables culling — the viewer uses a fake huge-frustum
 *     camera to keep everything always considered "in view")
 */
export async function loadGltfFromUrl(opts: {
  url: string;
  modelId: string;
  scene: THREE.Scene;
  renderer?: THREE.WebGLRenderer;
}): Promise<THREE.Group> {
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(opts.url);
  return finalizeGltf(gltf, opts);
}

/**
 * Parse a GLB/GLTF that has already been downloaded as an ArrayBuffer
 * (e.g. retrieved from the IndexedDB viewer cache) and attach it to the
 * scene. Same return shape as `loadGltfFromUrl`.
 *
 * The path heuristic: GLB is binary (starts with magic "glTF" 0x46546C67)
 * so we use parse() on the buffer directly. For .gltf JSON the buffer is
 * also accepted by GLTFLoader.parse — the second arg `path` is the base
 * URL used to resolve relative resource refs; we pass the original URL
 * directory so external buffers/textures still resolve.
 */
export async function loadGltfFromBuffer(opts: {
  buffer: ArrayBuffer;
  url: string;
  modelId: string;
  scene: THREE.Scene;
  renderer?: THREE.WebGLRenderer;
}): Promise<THREE.Group> {
  const loader = new GLTFLoader();
  const path = opts.url.substring(0, opts.url.lastIndexOf("/") + 1) || "";
  const gltf = await new Promise<any>((resolve, reject) => {
    loader.parse(opts.buffer, path, resolve, reject);
  });
  return finalizeGltf(gltf, opts);
}

function finalizeGltf(
  gltf: { scene: THREE.Group },
  opts: { modelId: string; scene: THREE.Scene; renderer?: THREE.WebGLRenderer }
): THREE.Group {
  const root = gltf.scene;
  root.name = opts.modelId;
  root.userData = {
    ...root.userData,
    echovViewerModelKind: "gltf" as const,
  };
  root.traverse((child: THREE.Object3D) => {
    (child as any).frustumCulled = false;
  });
  opts.scene.add(root);
  if (opts.renderer) {
    attachGltfMeshesToRendererClipping(root, opts.renderer);
  }
  return root;
}

export function disposeGltfSubtree(root: THREE.Object3D): void {
  root.traverse((child: THREE.Object3D) => {
    const mesh = child as THREE.Mesh;
    if ((mesh as any).geometry) {
      try {
        mesh.geometry?.dispose?.();
      } catch {
        /* noop */
      }
    }
    const mat = mesh.material;
    if (!mat) return;
    const list = Array.isArray(mat) ? mat : [mat];
    for (const m of list) {
      const mm = m as THREE.Material & { map?: THREE.Texture | null };
      try {
        mm.map?.dispose?.();
      } catch {
        /* noop */
      }
      try {
        mm.dispose?.();
      } catch {
        /* noop */
      }
    }
  });
  root.parent?.remove(root);
}
